/**
 * D282: one live session, kept current over the relay. `sessions.subscribe` is a long poll (the relay is
 * request and answer): each call waits up to 20 s for events and answers the opaque `after` to send back.
 * `resync` replaces what is drawn with the answer's snapshot; an event whose `seq` is already held is
 * dropped; `retryAfterMs` (too many live views wait on the workbench) is honoured before asking again.
 *
 * Gaps: the relay's 409 `offline` means the workbench is asleep or away, and the loop asks again every
 * 15 s; a call that times out or fails in transit is retried after 5 s, 15 s, then every 60 s, and the
 * page shows "Reconnecting" meanwhile. The loop ends when the page closes (the call in flight is aborted).
 *
 * The control calls (send, answer a question, interrupt, approve, stop, end remote control, pause the board, hand off,
 * press a block) carry `via: "web"`; a refusal is
 * JSON-RPC -32003 with the workbench's own sentence, shown as it is.
 */

import type { ApprovalAnswer, GitDiffAnswer, HandOffTarget, LiveApproval, LiveRead, RemoteState, UiAction, UiValues } from '@aioe/shared';
import { gitDiffAnswerSchema, handOffParams, turnDiffParams, UI_PRESS_UNSUPPORTED, uiPressParams } from '@aioe/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, problemText, routes } from '../../api/client.ts';
import type { PressOutcome } from './LiveBlocks.tsx';
import { applyEvent, EMPTY_LIVE, fromSnapshot, type LiveState, type LiveTarget } from './liveModel.ts';

export type LiveConnection = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'refused';

/** A JSON-RPC error from the workbench: -32003 carries a sentence for the person. */
export class LiveRefusal extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
  }
}

const WAIT_MS = 20_000;
const OFFLINE_RETRY_MS = 15_000;
const BACKOFF_MS = [5_000, 15_000, 60_000];

/** A relay call: the JSON-RPC `result`, or a LiveRefusal for its `error`. */
export async function liveCall<T>(deviceId: string, method: string, params: Record<string, unknown>, workspace?: string, signal?: AbortSignal): Promise<T> {
  const res = (await routes.rpc(deviceId, method, params, workspace, signal)) as { result?: T; error?: { code: number; message: string } } | undefined;
  if (res?.error) throw new LiveRefusal(res.error.code, res.error.message);
  return res?.result as T;
}

/** A failed call in a sentence: the workbench's own words for a refusal; the relay's offline in plain words. */
export function liveProblem(e: unknown, workbench = 'The workbench'): string {
  if (e instanceof LiveRefusal) {
    if (e.code === -32001) return 'This workbench has not given the organisation permission to do that.';
    const m = e.message.trim();
    return m ? `${m[0]!.toUpperCase()}${m.slice(1)}${/[.!?]$/.test(m) ? '' : '.'}` : 'The workbench refused this.';
  }
  if (isOffline(e)) return `${workbench} is not reachable.`;
  if (e instanceof ApiError && (e.status === 504 || e.code === 'timeout')) return `${workbench} did not answer in time. Try again in a moment.`;
  return problemText(e);
}

const isOffline = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'offline';
const isAbort = (e: unknown) => (e instanceof DOMException && e.name === 'AbortError') || (e instanceof Error && e.name === 'AbortError');

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const t = setTimeout(done, ms);
    function done() {
      clearTimeout(t);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

/** What the hook holds, for the stream it was read from: another session (or machine) starts afresh. */
interface Held {
  for: string;
  state: LiveState;
  connection: LiveConnection;
  /** The sentence for a refusal of the stream itself (remote control is off, not yours to watch). */
  problem?: string;
  /** The workbench answered `ui.press` with method-not-found (before D414): its UI blocks are read only here. */
  noPress?: boolean;
}
const fresh = (key: string): Held => ({ for: key, state: EMPTY_LIVE, connection: 'connecting' });

export function useLiveSession(deviceId: string | undefined, target: LiveTarget | undefined, opts: { workspace?: string; workbench?: string } = {}) {
  const sessionId = target?.sessionId;
  const conversationId = target?.conversationId;
  const workspace = opts.workspace;
  const streamKey = JSON.stringify([deviceId, sessionId, conversationId, workspace]);
  const [held, setHeld] = useState<Held>(() => fresh(streamKey));
  const current = held.for === streamKey ? held : fresh(streamKey);
  const workbench = useRef(opts.workbench);
  useEffect(() => {
    workbench.current = opts.workbench;
  });

  /** A change to what this stream holds; one for a stream since left is dropped. */
  const update = useCallback(
    (key: string, fn: (h: Held) => Held) => setHeld((prev) => fn(prev.for === key ? prev : fresh(key))),
    [],
  );

  useEffect(() => {
    if (!deviceId || (!sessionId && !conversationId)) return;
    const ctrl = new AbortController();
    const which = conversationId ? { conversationId } : { sessionId };
    const key = streamKey;
    void (async () => {
      let after: string | undefined;
      let failures = 0;
      while (!ctrl.signal.aborted) {
        const started = Date.now();
        try {
          const read = await liveCall<LiveRead>(deviceId, 'sessions.subscribe', { ...which, ...(after ? { after } : {}), waitMs: WAIT_MS, limit: 200, turns: 100 }, workspace, ctrl.signal);
          if (ctrl.signal.aborted) return;
          failures = 0;
          if (read?.after) after = read.after;
          update(key, (h) => {
            let s = read?.resync ? (read.snapshot ? fromSnapshot(read.snapshot, read.remote, sessionId, h.state) : { ...EMPTY_LIVE, ready: true }) : h.state;
            for (const e of read?.events ?? []) s = applyEvent(s, e, sessionId);
            return { for: key, connection: 'live', state: { ...s, ready: true, ...(read?.remote ? { remote: read.remote } : {}) } };
          });
          if (read?.retryAfterMs) await pause(read.retryAfterMs, ctrl.signal);
          else if (!read?.more && !read?.events?.length && Date.now() - started < 1_000) await pause(1_000, ctrl.signal); // a workbench that answers at once: never spin
        } catch (e) {
          if (ctrl.signal.aborted || isAbort(e)) return;
          if (isOffline(e)) {
            update(key, (h) => ({ ...h, connection: 'offline', problem: undefined }));
            await pause(OFFLINE_RETRY_MS, ctrl.signal);
          } else if (e instanceof LiveRefusal || (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 408 && e.status !== 429)) {
            update(key, (h) => ({ ...h, connection: 'refused', problem: liveProblem(e, workbench.current) }));
            await pause(OFFLINE_RETRY_MS, ctrl.signal);
          } else {
            update(key, (h) => ({ ...h, connection: 'reconnecting', problem: undefined }));
            await pause(BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)]!, ctrl.signal);
            failures++;
          }
        }
      }
    })();
    return () => ctrl.abort();
  }, [deviceId, sessionId, conversationId, workspace, streamKey, update]);

  /** One control call with `via: "web"`; answers the sentence to show when it was refused, else undefined. */
  const act = useCallback(
    async (method: string, params: Record<string, unknown> = {}): Promise<string | undefined> => {
      if (!deviceId) return 'This session is not on a workbench the console knows.';
      try {
        const result = await liveCall<unknown>(deviceId, method, { ...(conversationId ? { conversationId } : { sessionId }), ...params, via: 'web' }, workspace);
        if (method === 'sessions.remote' && result && typeof result === 'object' && 'on' in result) update(streamKey, (h) => ({ ...h, state: { ...h.state, remote: result as RemoteState } }));
        return undefined;
      } catch (e) {
        if (isOffline(e)) update(streamKey, (h) => ({ ...h, connection: 'offline' }));
        return liveProblem(e, workbench.current);
      }
    },
    [deviceId, sessionId, conversationId, workspace, streamKey, update],
  );

  const send = useCallback((text: string) => act('sessions.send', { text }), [act]);
  /** Answer a question an agent asked in the conversation (D372): only the person asked, or someone covering them; anyone else is refused in Team Work's words. */
  const answer = useCallback((questionId: string, text: string) => act('sessions.send', { text, intent: 'answer', questionId }), [act]);
  /**
   * The task strip's Pause all / Resume all (D372 `board.setPaused`): needs the machine's task-board scope and this
   * session open to remote control; the workbench refuses it otherwise. The stream does not say a board is paused, so
   * what was asked from here is kept.
   */
  const setPaused = useCallback(
    async (teamId: string, paused: boolean) => {
      const problem = await act('board.setPaused', { teamId, paused });
      if (!problem) update(streamKey, (h) => ({ ...h, state: { ...h.state, paused } }));
      return problem;
    },
    [act, update, streamKey],
  );
  /** The usage limit's "Hand off to…" (D357 `sessions.handOff`): the agent session moves to another runtime for the rest of the session. */
  const handOff = useCallback((agentSession: string, target: HandOffTarget) => act('sessions.handOff', handOffParams(agentSession, target)), [act]);
  /**
   * D414: one file of a finished turn, as that turn changed it (`git.diff { rootId, checkpoint, path }`, agents:read). A
   * read, not a control: no `via`. Throws a LiveRefusal with the workbench's sentence, or the relay's error.
   */
  const readDiff = useCallback(
    async (item: { rootId: string; checkpoint: string }, path: string): Promise<GitDiffAnswer> => {
      if (!deviceId) throw new Error('This session is not on a workbench the console knows.');
      return gitDiffAnswerSchema.parse(await liveCall<unknown>(deviceId, 'git.diff', turnDiffParams(item, path, { conversationId, sessionId }), workspace));
    },
    [deviceId, conversationId, sessionId, workspace],
  );
  /**
   * D414: a press on a generative UI block (`ui.press { conversationId, messageId, blockId, action, values?, via }`): the
   * block's own action object and the fields' values. A workbench before D414 answers -32601: the stream's blocks are
   * read only from then on.
   */
  const press = useCallback(
    async (messageId: string, blockId: string, action: UiAction, values: UiValues): Promise<PressOutcome> => {
      if (!deviceId || !conversationId) return { ok: false, problem: 'This block is answered on the workbench.' };
      try {
        await liveCall<unknown>(deviceId, 'ui.press', { conversationId, ...uiPressParams(messageId, blockId, action, values), via: 'web' }, workspace);
        return { ok: true };
      } catch (e) {
        if (e instanceof LiveRefusal && e.code === UI_PRESS_UNSUPPORTED) {
          update(streamKey, (h) => ({ ...h, noPress: true }));
          return { ok: false, unsupported: true };
        }
        if (isOffline(e)) update(streamKey, (h) => ({ ...h, connection: 'offline' }));
        return { ok: false, problem: liveProblem(e, workbench.current) };
      }
    },
    [deviceId, conversationId, workspace, streamKey, update],
  );
  const interrupt = useCallback(() => act('sessions.interrupt'), [act]);
  const stop = useCallback(() => act('sessions.stop'), [act]);
  const endRemote = useCallback(() => act('sessions.remote', { on: false }), [act]);
  /**
   * Approve, decline or allow for the session (the workbench's card answers, `ApprovalAnswer`): the option the answer
   * names, else the request's own option of that kind.
   */
  const resolve = useCallback(
    async (a: LiveApproval, answer: ApprovalAnswer | 'allow' | 'deny') => {
      const { decision, optionId } = typeof answer === 'string' ? { decision: answer, optionId: undefined } : answer;
      const option = optionId ?? a.options?.find((o) => (decision === 'allow' ? o.kind === 'allow_once' : o.kind === 'reject_once'))?.id;
      const problem = await act('permissions.resolve', { ...(a.sessionId ? { sessionId: a.sessionId } : {}), permissionId: a.permissionId, decision, ...(option ? { optionId: option } : {}) });
      // The stream's `approval.answered` takes the card away everywhere; this takes it away here at once.
      if (!problem) update(streamKey, (h) => ({ ...h, state: { ...h.state, approvals: h.state.approvals.filter((x) => x.permissionId !== a.permissionId) } }));
      return problem;
    },
    [act, update, streamKey],
  );

  return { state: current.state, connection: current.connection, problem: current.problem, conversationId, canPress: Boolean(conversationId) && !current.noPress, send, answer, setPaused, handOff, readDiff, press, interrupt, stop, endRemote, resolve };
}
